import { useGetSavedProductPricesQuery, useSelectedOrg } from "@fero/hooks";
import type { Currency } from "@fero-shared/api-contracts";
import { Dropdown } from "components/widgets/dropdown/dropdown";
import type React from "react";
import { useEffect, useRef, useState } from "react";

type UseCurrencySelectReturn = {
  currency: string;
  CurrencySelect: React.FC;
  currencySelectorEnabled: boolean;
};

const useCurrencySelect = (organisationId: string): UseCurrencySelectReturn => {
  const [currency, setCurrency] = useState<Currency>("GBP");
  const selectedOrg = useSelectedOrg();
  const { data: savedPrices } = useGetSavedProductPricesQuery({
    organisationId,
    purchaserId: null,
  });

  const uniqueCurrencies = Array.from(
    new Set(
      savedPrices && Array.isArray(savedPrices.body)
        ? savedPrices.body.map((price) => price.currency)
        : [],
    ),
  );
  const uniqueCurrenciesRef = useRef(uniqueCurrencies);
  useEffect(() => {
    uniqueCurrenciesRef.current = uniqueCurrencies;
  }, [uniqueCurrencies]);

  // Default to org currency or first found one
  useEffect(() => {
    if (uniqueCurrenciesRef.current.length > 0) {
      const defaultCurrency =
        selectedOrg?.value.currency || uniqueCurrenciesRef.current[0];
      setCurrency(defaultCurrency);
    }
  }, [selectedOrg]);

  const menuItems = uniqueCurrencies.map((cur) => ({ label: cur, value: cur }));

  const CurrencySelect: React.FC = () => (
    <Dropdown
      inputLabel="Currency"
      menuItems={menuItems}
      onChange={(selectedCurrency) => {
        if (selectedCurrency) {
          setCurrency(selectedCurrency as Currency); // Handle currency change
        }
      }}
      defaultValue={{ label: currency, value: currency }}
      resettable={false}
      fullWidth={false}
      minWidth={"100px"}
    />
  );
  const currencySelectorEnabled = uniqueCurrencies.length !== 0;
  return { currency, CurrencySelect, currencySelectorEnabled };
};

export default useCurrencySelect;
